import React, { useEffect, useState } from 'react'
import { useTranslation } from "react-i18next"
import { Container, Row, Col, Card, Modal, OverlayTrigger, Tooltip, Button } from 'react-bootstrap'
import { useDispatch, useSelector } from 'react-redux'
import { editInscription, getInscriptionsByClub } from '../../../../actions/activities/inscriptionsActions'
import { CustomDataTable } from '../../../../components/design/Datatables/CustomDataTable'
import ActivitiesSubMenu from '../../../../components/menus/ActivitiesSubMenu'
import InscriptionSummary from '../../../../components/Activities/InscriptionSummary'
import useAuth from '../../../../context/auth/useAuth'
import Swal from 'sweetalert2'
import ClubBreadCrumbs from '../../../../components/breadcrumbs/ClubBreadCrumbs'
import Loader from '../../../../components/design/Loader/Loader'
import * as XLSX from 'xlsx'

export default function Inscriptions({usertype, inscriptionselected, handleInscriptionSelect}) {
    const { t } = useTranslation()
    const dispatch = useDispatch()
	const {clientauth} = useAuth()

    const [show, setShow] = useState(false)
    const handleClose = () => setShow(false)
    const {inscriptions, loadinginscriptions, pagination} = useSelector(state=>state.inscriptions)
    const {club} = useSelector(state=>state.clubs)

    const [typeform, setTypeform] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const [rowsPerPage, setRowsPerPage] = useState(25)
    const [allRowsSelected, setAllRowsSelected] = useState(false)
    const [searchInput, setSearchInput] = useState('')
    const [debouncedSearch, setDebouncedSearch] = useState('')
    const translatedInscriptions = inscriptions?.map(inscription => ({
        ...inscription,
        translatedStatus: t('inscriptions.status' + inscription.status),
    }))

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'instant',
        })
    }, [])

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setDebouncedSearch(searchInput.trim())
        }, 350)

        return () => clearTimeout(timeoutId)
    }, [searchInput])

    useEffect(() => {
        const requestLimit = allRowsSelected ? -1 : rowsPerPage
        if(clientauth){
            dispatch(getInscriptionsByClub(club?._id, false, currentPage, requestLimit, debouncedSearch))
        }
    },[dispatch, clientauth, club?._id, currentPage, rowsPerPage, debouncedSearch, allRowsSelected])

    useEffect(() => {
        setCurrentPage(1)
    }, [club?._id])

    const handlePageChange = (page) => {
        setCurrentPage(page)
    }

    const handleRowsPerPageChange = (newPerPage) => {
        const perPageNumber = Number(newPerPage)
        const totalRows = Number(pagination?.total || 0)
        const hasNumericPerPage = Number.isFinite(perPageNumber) && perPageNumber > 0
        const normalizedPerPageValue = String(newPerPage || '').trim().toLowerCase()
        const showAll =
            normalizedPerPageValue === 'all' ||
            normalizedPerPageValue === 'todos' ||
            newPerPage === -1 ||
            !hasNumericPerPage ||
            (totalRows > 0 && hasNumericPerPage && perPageNumber >= totalRows)
        setAllRowsSelected(showAll)
        setRowsPerPage(hasNumericPerPage ? perPageNumber : (totalRows > 0 ? totalRows : 25))
        setCurrentPage(1)
    }

    const handleSelectChange = (inscription, value) => {
        if(value==='3'){
            Swal.fire({
                showConfirmButton: true,
                showCancelButton: true,
                confirmButtonText: t('global.yes'),
                cancelButtonText: t('global.no'),
                icon: 'warning',
                text: t('inscriptions.cancelSecure')
            }).then((newresult) => {
                if (newresult.isConfirmed) {
                    dispatch(editInscription(inscription, value, 'status', 'club'))
                }
            })
        }else{
            dispatch(editInscription(inscription, value, 'status', 'club'))
        }
    }
    const formatDate = (dateString) => {
        const date = new Date(dateString)
        return date.toLocaleDateString('es-ES')
    }

    const columnsInscriptions = [
        {
            name: t('inscriptions.client'),
            cell: inscription =>
                <div
                    onClick={() => {
                        handleInscriptionSelect(inscription._id)
                        setShow(true)
                    }}
                    className='table-link'>
                    {inscription?.client?.firstname} {inscription?.client?.lastname}
                </div>,
            selector: (inscription) => inscription.firstname+' '+inscription.lastname,
            sortable: true,
        },{
            name: t('users.email'),
            cell: inscription => inscription?.clientEmail || '',
            selector: (inscription) => inscription?.clientEmail || '',
            sortable: true,
        },{
            name: t('users.phone'),
            cell: inscription => inscription?.client?.phone || '',
            selector: (inscription) => inscription?.client?.phone || '',
            sortable: true,
        },{
            name: <>{t('groups.activity')} / {t('groups.category')}</>,
            cell: inscription =>
                <div
                    onClick={() => {
                        handleInscriptionSelect(inscription._id)
                        setShow(true)
                    }}
                    className='table-link'>
                    {inscription?.activity?.title} / {inscription?.category?.title}
                </div>,
            selector: (inscription) => inscription?.activity?.title+' '+inscription?.category?.title,
            sortable: true,
        },{
            name: <>{t('inscriptions.group')} / {t('inscriptions.horary')}</>,
            cell: inscription =>
                <>{inscription?.group?.title} / {inscription?.horary?.name}</>,
            selector: (inscription) => inscription?.group?.title+' '+inscription?.horary?.name,
            sortable: true,
        },{
            name: t('inscriptions.status'),
            center: 'true',
            cell: inscription => inscription.translatedStatus,
            selector: (inscription) => inscription.translatedStatus,
            sortable: true,
        },{
            name: t('inscriptions.paymentStatus'),
            center: 'true',
            cell: inscription =>
                !inscription.paymentStatus && inscription.paymentStatus !== 0
                    ?
                        <></>
                    :
                        <span className={`p-2 text-white ${inscription.paymentStatus === 0 ? 'bg-success' : inscription.paymentStatus === 1 ? 'bg-warning' : 'bg-danger'}`}>
                            {
                                inscription.paymentStatus === 0
                                    ?
                                        <i className='fas fa-check'/>
                                    :
                                        inscription.paymentStatus === 1
                                            ?
                                                <i className='fas fa-clock'/>
                                            :
                                                <i className='fas fa-x'/>
                            }
                        </span>
            ,
            selector: (inscription) => inscription.paymentStatus === 0 ? 'success' : inscription.paymentStatus === 1 ? 'warning' : 'danger',
            sortable: true,
        },{
            name: t('inscriptions.actions'),
            center: 'true',
            width:'120px',
            cell: inscription =>
                <div className='d-flex w-100'>
                    <div
                        onClick={() => {
                            handleInscriptionSelect(inscription._id)
                            setTypeform('view')
                            setShow(true)
                        }}
                        className='table-link me-2'>
                        <OverlayTrigger placement="top"
                            overlay={<Tooltip id="tooltip">{t('inscriptions.seeinscription')}</Tooltip>}
                        >
                            <img className="cursor" src="/images/eyeopen.svg" alt="eyes" height={16}/>
                        </OverlayTrigger>
                    </div>
                    <div
                        onClick={() => {
                            handleInscriptionSelect(inscription._id)
                            setTypeform('edit')
                            setShow(true)
                        }}
                        className='table-link'>
                        <OverlayTrigger placement="top"
                            overlay={<Tooltip id="tooltip">{t('inscriptions.editinscription')}</Tooltip>}
                        >
                            <img className="cursor" src="/images/pencil.svg" alt="pencil" height={16}/>
                        </OverlayTrigger>
                    </div>
                    {inscription?.status === "0" || inscription?.status === "4" ?
                        <>
                            <OverlayTrigger placement="top"
                                overlay={<Tooltip id="tooltip">{t('inscriptions.approveinscription')}</Tooltip>}
                            >
                                <i className="fas fa-check px-2 cursor" onClick={() => handleSelectChange(inscription?._id, "1")}></i>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top"
                                overlay={<Tooltip id="tooltip">{t('inscriptions.cancelinscription')}</Tooltip>}
                            >
                                <i className="fas fa-xmark px-2 cursor" onClick={() => handleSelectChange(inscription?._id, "3")}></i>
                            </OverlayTrigger>
                        </>
                    :
                        inscription?.status !== "3" ?
                            <OverlayTrigger placement="top"
                                overlay={<Tooltip id="tooltip">{t('inscriptions.cancelinscription')}</Tooltip>}
                            >
                                <i className="fas fa-xmark px-2 cursor" onClick={() => handleSelectChange(inscription?._id, "3")}></i>
                            </OverlayTrigger>
                        :
                            ''
                    }
                </div>,
            selector: (inscription) => inscription?.group?.title,
            sortable: false,
        }
	]

    const exportToExcel = () => {
        if (!inscriptions || inscriptions.length === 0) return

        const dataToExport = inscriptions.map(inscription => {
            return {
                [t('inscriptions.client')]: `${inscription?.client?.firstname || ''} ${inscription?.client?.lastname || ''}`,
                [t('users.email')]: inscription?.clientEmail || '',
                [t('users.phone')]: inscription?.client?.phone || '',
                [`${t('groups.activity')} / ${t('groups.category')}`]: `${inscription?.activity?.title || ''} / ${inscription?.category?.title || ''}`,
                [`${t('inscriptions.group')} / ${t('inscriptions.horary')}`]: `${inscription?.group?.title || ''} / ${inscription?.horary?.name || ''}`,
                [t('inscriptions.status')]: t('inscriptions.status' + inscription.status),
                [t('inscriptions.policy')]: inscription?.policy ? t('global.yes') : t('global.no'),
                [t('inscriptions.minorsPolicy')]: inscription?.minorsPolicy ? t('global.yes') : t('global.no'),
                [t('inscriptions.sendMarketPolicy')]: inscription?.sendMarketPolicy ? t('global.yes') : t('global.no')
                // Excluding "acciones" (actions) and "Estado de pagos" (payment status) columns
            }
        })

        const ws = XLSX.utils.json_to_sheet(dataToExport)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Inscripciones')
        
        const fileName = `inscripciones_${club?.name}_${new Date().toISOString().split('T')[0]}.xlsx`
        XLSX.writeFile(wb, fileName)
    }

    const ExpandedRow = ({ data }) => {
        return (
            <Container className='p-3'>
                <Row>
                    <Col xs={12} lg={6}>
                        <div className='d-flex justify-content-between border-1 border-bottom p-2'>
                            <small>{t('users.birthdate')}</small> {formatDate(data?.client?.birthdate)}
                        </div>
                        <div className='d-flex justify-content-between border-1 border-bottom p-2'>
                            <small>{t('users.phone')}</small> {data?.client?.phone}
                        </div>
                        <div className='d-flex justify-content-between border-1 border-bottom p-2'>
                            <small>{t('users.emergencyphone')}</small> {data?.client?.emergencyPhone}
                        </div>
                        <div className='d-flex justify-content-between border-1 border-bottom p-2'>
                            <small>{t('users.email')}</small> {data?.clientEmail}
                        </div>
                        {data?.client?.identityDocuments?.length > 0
                        ?
                            <div className='d-flex flex-column border-1 border-bottom p-2'>
                                {data?.client?.identityDocuments?.map((doc, index) => (
                                    <div key={index} className='d-flex justify-content-between'>
                                        <small className='text-uppercase'>{doc.documentType}</small>{doc.documentNumber}
                                    </div>
                                ))}
                            </div>
                         :
                            <></>
                        }
                         {data?.client?.addresses?.length > 0
                        ?
                            <div className='d-flex flex-column border-1 border-bottom p-2'>
                                {data?.client?.addresses?.map((address, index) => (
                                    <div key={index} className='d-flex justify-content-between'>
                                        <small className='text-uppercase'>{t('menus.config.address')}</small>
                                        <div className='text-end'>
                                            {address.address}<br/>
                                            {address.town} ({address.province})<br/>
                                            {address.postalCode}
                                        </div>
                                    </div>
                                ))}
                            </div>
                         :
                            <></>
                        }
                    </Col>
                    <Col xs={12} lg={6}>
                        {data?.tutor1Name
                            ?
                                <>
                                    <div className='d-flex justify-content-between border-1 border-bottom p-2'>
                                        <small>{t('users.tutor1Name')}</small> {data?.tutor1Name}
                                    </div>
                                    <div className='d-flex justify-content-between border-bottom p-2'>
                                        <small>{t('users.tutor1Phone')}:</small> {data?.tutor1Phone}
                                    </div>
                                    <div className='d-flex justify-content-between border-1 border-bottom p-2'>
                                        <small>{t('users.tutor1Email')}</small> {data?.tutor1Email}
                                    </div>
                                    <div className='d-flex justify-content-between border-1 border-bottom p-2'>
                                        <small>{t('users.tutor2Name')}</small> {data?.tutor2Name}
                                    </div>
                                    <div className='d-flex justify-content-between border-bottom p-2'>
                                        <small>{t('users.tutor2Phone')}:</small> {data?.tutor2Phone}
                                    </div>
                                    <div className='d-flex justify-content-between border-1 border-bottom p-2'>
                                        <small>{t('users.tutor2Email')}</small> {data?.tutor2Email}
                                    </div>
                                </>
                            :
                                <></>
                        }
                    </Col>
                </Row>
            </Container>
        )
    }
    return (
        <>
            <Container className='cuerpo'>
                <ClubBreadCrumbs usertype={usertype} club={club} />
                <Row>
                    <Col className='d-flex align-items-center'>
                        <h1 className='ms-2 my-0'>{t('menus.activities.inscriptions')}</h1>
                    </Col>
                </Row>
                <Row className="my-3">
                    <Col>
                        <ActivitiesSubMenu usertype={usertype} active="inscriptions"/>
                    </Col>
                </Row>
                <Row className="my-3">
                    <Col>
                        <Card>
                            {loadinginscriptions
                            ?
                                <Loader/>
                            :
                                <Card.Body>
                                    <div className="my-3">
                                        <Row>
                                            <Col>
                                                <div className="d-flex justify-content-between align-items-end gap-2 mb-3 flex-wrap">
                                                    <div style={{flex: 1}}></div>
                                                    <div className='d-flex flex-column align-items-center' style={{flex: 1}}>
                                                        <h2 className='mb-1'>Buscador</h2>
                                                        <div className='data-table-extensions-filter'>
                                                            <span className='icon' />
                                                            <input
                                                                type='text'
                                                                className='filter-text'
                                                                placeholder={t('datatables.buscar')}
                                                                value={searchInput}
                                                                onChange={(e) => {
                                                                    setSearchInput(e.target.value)
                                                                    setCurrentPage(1)
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className='d-flex justify-content-end' style={{flex: 1}}>
                                                        <div className="d-flex justify-content-end mb-0">
                                                            <Button variant="success" onClick={exportToExcel}>
                                                                <i className="fas fa-file-excel me-2"></i>
                                                                {t('global.exportToExcel')}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </div>
                                                {inscriptions?.length > 0 ?
                                                    <>
                                                        <CustomDataTable
                                                            key={`${club?._id || 'club'}-${rowsPerPage}-${allRowsSelected ? 'all' : 'paged'}`}
                                                            columns={columnsInscriptions}
                                                            data={translatedInscriptions}
                                                            exportable={false}
                                                            printable={false}
                                                            expandedData={ExpandedRow}
                                                            paginationServer={true}
                                                            paginationTotalRows={pagination?.total || 0}
                                                            paginationPerPage={allRowsSelected ? Number(pagination?.total || rowsPerPage || 25) : rowsPerPage}
                                                            paginationDefaultPage={currentPage}
                                                            onChangePage={handlePageChange}
                                                            onChangeRowsPerPage={handleRowsPerPageChange}
                                                        />
                                                    </>
                                                :
                                                    <div className='contenedor-vacio text-center'>
                                                        <h4>{t('inscriptions.thereisnotinscriptions')}</h4>
                                                        {t('inscriptions.thereisnotinscriptionsphrase')}
                                                    </div>
                                                }
                                            </Col>
                                        </Row>
                                    </div>
                                </Card.Body>
                            }
                        </Card>
                    </Col>
                </Row>
            </Container>
            <Modal show={show} onHide={handleClose} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <h2>{t('inscriptions.datainscription')}</h2>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className='cuerpo px-5 py-4'>
                    <InscriptionSummary
                        usertype={usertype}
                        typeform={typeform}
                        inscriptionselected={inscriptionselected}
                        closeModal={handleClose}
                    />
                </Modal.Body>
            </Modal>
        </>
    )
}